import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Card, Typography, Stack, Chip, Switch, Tooltip, Button, Alert, Grid,
  CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, Table,
  TableHead, TableRow, TableCell, TableBody, TableContainer, alpha, useTheme, Skeleton,
} from '@mui/material';
import { BellRing, ShieldCheck, Eye, ArrowRight, PhoneCall, CheckCircle2, Send } from 'lucide-react';
import { waApi } from './whatsapp/waApi';

// Admin card for the onboarding auto-reminder engine (backend:
// onboardingNudgeService.js). Lives on the Follow-ups page because the team's
// call list is exactly the people this automation could not convert.

const STAGE_LABEL = {
  unverified: 'Verify OTP',
  business_pending: 'Add business',
  product_pending: 'Add product',
};

const fmtOffset = (mins) => {
  if (mins < 60) return `${mins} min`;
  const days = mins / 1440;
  return Number.isInteger(days) ? `${days} day${days > 1 ? 's' : ''}` : `${Math.round(mins / 60)} h`;
};

const TPL_COLOR = { APPROVED: 'success', PENDING: 'warning', REJECTED: 'error', MISSING: 'default' };

const ACTION_LABEL = {
  send: 'Send now',
  handoff: 'Hand to team',
  wait: 'Waiting',
  stop: 'No message',
};
const REASON_LABEL = {
  not_due: 'not due yet',
  min_gap: '24h gap',
  quiet_hours: 'quiet hours (9pm–9am)',
  recent_outbound: 'team messaged recently',
  awaiting_handoff: '48h after last reminder',
  opted_out: 'replied STOP',
  replied: 'replied — needs a human',
  handed_off: 'already with the team',
  no_contact: 'no phone or email',
  all_reminders_unanswered: '3 reminders, no progress',
  backlog_reminder_unanswered: 'backlog reminder, no progress',
};

const Stat = ({ label, value, icon, color }) => (
  <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: alpha(color, 0.08), height: '100%' }}>
    <Stack direction="row" spacing={1} alignItems="center" sx={{ color }}>
      {icon}
      <Typography sx={{ fontSize: 12, fontWeight: 700 }}>{label}</Typography>
    </Stack>
    <Typography sx={{ fontSize: 22, fontWeight: 800, mt: 0.5 }}>{value}</Typography>
  </Box>
);

const OnboardingAutoReminders = ({ onChanged }) => {
  const theme = useTheme();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [preview, setPreview] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setStatus(await waApi.getOnboardingAuto());
      setError('');
    } catch (e) {
      setError(e?.response?.data?.msg || 'Could not load auto-reminder status.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (enabled) => {
    setSaving(true);
    setError('');
    try {
      const r = await waApi.setOnboardingAuto({ enabled });
      setStatus(r.status);
      setNotice(r.msg);
      onChanged?.();
    } catch (e) {
      setError(e?.response?.data?.msg || 'Could not update.');
    } finally {
      setSaving(false);
    }
  };

  const openPreview = async () => {
    setPreviewOpen(true);
    setPreviewLoading(true);
    try {
      setPreview(await waApi.previewOnboardingAuto());
    } catch (e) {
      setPreview({ error: e?.response?.data?.msg || 'Could not build the preview.' });
    } finally {
      setPreviewLoading(false);
    }
  };

  if (loading && !status) {
    return <Skeleton variant="rounded" height={180} sx={{ borderRadius: 3, mb: 2.5 }} />;
  }
  if (!status) {
    return error ? <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>{error}</Alert> : null;
  }

  const green = theme.palette.success.main;
  const on = status.enabled && !status.env_stopped;
  const sent = status.last_7_days?.sent || {};
  const conv = status.last_7_days?.converted || {};
  const sum = (o, f) => Object.values(o || {}).reduce((a, v) => a + (f ? f(v) : v || 0), 0);
  const notReadyTip = status.templates_ready
    ? ''
    : 'Waiting for Meta to approve all 6 WhatsApp templates — the switch unlocks automatically once they are approved.';

  return (
    <Card sx={{ p: { xs: 2, md: 2.5 }, mb: 2.5, borderRadius: 3, border: `1px solid ${alpha(on ? green : theme.palette.divider, on ? 0.4 : 1)}` }}>
      {/* Header */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'flex-start', sm: 'center' }} sx={{ mb: 1.5 }}>
        <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: alpha('#25D366', 0.14), color: '#128C7E', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <BellRing size={20} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography sx={{ fontWeight: 800, fontSize: '1.05rem' }}>Automatic onboarding reminders</Typography>
            <Chip size="small" label={on ? 'ON' : 'OFF'} color={on ? 'success' : 'default'} sx={{ fontWeight: 800 }} />
            {!status.live_send && <Chip size="small" color="warning" label="WhatsApp in simulation mode" />}
          </Stack>
          <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
            WhatsApp (+ email on the 2nd reminder) to users stuck at a step. Max 3 per step, never more than 1 per day, nothing between 9pm–9am — then they land in this list for a call.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button size="small" variant="outlined" startIcon={<Eye size={15} />} onClick={openPreview} sx={{ fontWeight: 700 }}>
            Preview next run
          </Button>
          <Tooltip title={notReadyTip}>
            <span>
              <Switch
                checked={Boolean(status.enabled)}
                disabled={saving || (!status.enabled && !status.templates_ready)}
                onChange={(e) => toggle(e.target.checked)}
                inputProps={{ 'aria-label': 'Turn automatic onboarding reminders on or off' }}
                color="success"
              />
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 1.5, borderRadius: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {notice && <Alert severity="success" sx={{ mb: 1.5, borderRadius: 2 }} onClose={() => setNotice('')}>{notice}</Alert>}
      {status.env_stopped && (
        <Alert severity="warning" sx={{ mb: 1.5, borderRadius: 2 }}>
          Stopped on the server (ONBOARDING_NUDGE_CRON=off). The switch has no effect until that is removed.
        </Alert>
      )}
      {status.go_live_at && (
        <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 1.5 }}>
          Live since {new Date(status.go_live_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}.
          Users stuck before that (last 30 days only) get one reminder, max {status.backlog_daily_cap}/day.
        </Typography>
      )}

      {/* Timeline */}
      <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
        {(status.schedule || []).map((s) => (
          <Grid key={s.stage} size={{ xs: 12, md: 4 }}>
            <Box sx={{ p: 1.5, borderRadius: 2, border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
              <Typography sx={{ fontWeight: 800, fontSize: 13, mb: 0.75 }}>{s.label}</Typography>
              <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                {s.offsets_minutes.map((m, i) => (
                  <React.Fragment key={i}>
                    <Chip size="small" label={`${i + 1}: +${fmtOffset(m)}`} sx={{ fontWeight: 700, bgcolor: alpha('#25D366', 0.1) }} />
                    <ArrowRight size={12} />
                  </React.Fragment>
                ))}
                <Chip size="small" icon={<PhoneCall size={12} />} label="team call" variant="outlined" />
              </Stack>
              <Typography sx={{ fontSize: 11.5, color: 'text.secondary', mt: 0.75 }}>
                Active now: <b>{status.active?.[s.stage] || 0}</b> · sent (7d): <b>{sent[s.stage]?.total || 0}</b> · completed (7d): <b>{conv[s.stage] || 0}</b>
              </Typography>
            </Box>
          </Grid>
        ))}
      </Grid>

      {/* Stats */}
      <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
        <Grid size={{ xs: 6, md: 3 }}><Stat label="Reminders sent (7d)" value={sum(sent, (v) => v.total)} icon={<Send size={15} />} color={theme.palette.info.main} /></Grid>
        <Grid size={{ xs: 6, md: 3 }}><Stat label="Steps completed (7d)" value={sum(conv)} icon={<CheckCircle2 size={15} />} color={green} /></Grid>
        <Grid size={{ xs: 6, md: 3 }}><Stat label="Handed to team (open)" value={status.handed_off_open || 0} icon={<PhoneCall size={15} />} color={theme.palette.warning.main} /></Grid>
        <Grid size={{ xs: 6, md: 3 }}><Stat label="Failed sends (7d)" value={sum(sent, (v) => v.failed)} icon={<ShieldCheck size={15} />} color={theme.palette.error.main} /></Grid>
      </Grid>

      {/* Templates */}
      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap alignItems="center">
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'text.secondary', mr: 0.5 }}>Templates:</Typography>
        {(status.templates || []).map((t) => (
          <Tooltip key={t.name} title={t.warning || `${t.status}${t.category ? ` · ${t.category}` : ''}`}>
            <Chip size="small" variant={t.ok ? 'filled' : 'outlined'} color={TPL_COLOR[t.status] || 'default'} label={`${t.name} · ${t.status}`} sx={{ fontWeight: 600 }} />
          </Tooltip>
        ))}
      </Stack>

      {/* Preview dialog */}
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>What the next run would do</DialogTitle>
        <DialogContent dividers>
          {previewLoading ? (
            <Stack alignItems="center" sx={{ py: 5 }}><CircularProgress size={28} /></Stack>
          ) : preview?.error ? (
            <Alert severity="error">{preview.error}</Alert>
          ) : preview ? (
            <>
              <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 1.5 }}>
                Read-only — nothing is sent. {status.enabled ? '' : 'Shown as if the switch were turned on now.'}
              </Typography>
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                {Object.entries(preview.summary || {}).map(([k, v]) => {
                  const [a, r] = k.split(':');
                  return <Chip key={k} size="small" label={`${ACTION_LABEL[a] || a}${r ? ` (${REASON_LABEL[r] || r.replace(/_/g, ' ')})` : ''}: ${v}`} color={a === 'send' ? 'success' : a === 'handoff' ? 'warning' : 'default'} />;
                })}
              </Stack>
              <TableContainer sx={{ maxHeight: 420 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>User</TableCell>
                      <TableCell>Stuck at</TableCell>
                      <TableCell>Sent</TableCell>
                      <TableCell>Next action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(preview.plan || []).map((p) => (
                      <TableRow key={p.user_id}>
                        <TableCell sx={{ fontWeight: 600 }}>{p.name}</TableCell>
                        <TableCell>{STAGE_LABEL[p.stage] || p.stage}</TableCell>
                        <TableCell>{p.reminders_sent}/3</TableCell>
                        <TableCell>
                          {ACTION_LABEL[p.action] || p.action}
                          {p.action === 'send' && ` reminder ${p.step + 1}${p.backlog ? ' (backlog)' : ''} via ${(p.channels || []).join(' + ')}`}
                          {p.reason && ` — ${REASON_LABEL[p.reason] || p.reason.replace(/_/g, ' ')}`}
                          {p.dueAt && p.action === 'wait' && ` (${new Date(p.dueAt).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' })})`}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setPreviewOpen(false); load(); }}>Close</Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default OnboardingAutoReminders;
